/** Chamadas tipadas à API — um lugar só para saber quais rotas existem. */

import { api } from './client';
import type { AssinaturaDetalhe } from './plataforma';
import type {
  AgapeEntrada,
  AtaDetalhe,
  AtaEntrada,
  LeituraDaAta,
  AgendaItem,
  Arquivo,
  Conjuge,
  ConjugeEntrada,
  Contato,
  ContatoEntrada,
  ContraparteNaoVinculada,
  EnderecoPorCep,
  Filho,
  FilhoEntrada,
  Fornecedor,
  FornecedorDetalhe,
  FornecedorEntrada,
  TipoFornecedor,
  TipoSessao,
  MembroCadastro,
  MembroNovo,
  MembroCadastroEntrada,
  MunicipioOpcao,
  Uf,
  Ata,
  Evento,
  FluxoMensal,
  CoberturaTroncoEntrada,
  CoberturaTroncoResposta,
  Fraternidade,
  Gob,
  IrmaoParaEscolher,
  Lancamento,
  BeneficioDoMembro,
  AcessoDoIrmao,
  AcessoEntrada,
  PapelDeAcesso,
  BeneficioEntrada,
  GraduacaoItem,
  CompromissoEntrada,
  LeituraDaFoto,
  LeituraDosVisitantes,
  VisitanteDaSessao,
  VisitanteEntrada,
  LeituraDoCalendario,
  ItemDoCalendarioConfirmado,
  ResultadoImportacaoCalendario,
  LojaPublica,
  DatasImportantes,
  EnvioDeDatas,
  LojaDetalhe,
  IdentidadeDaLoja,
  MembroGraduacaoLinha,
  MembroLista,
  MembroPerfil,
  PosicaoFinanceira,
  PainelPresencas,
  ContaBancaria,
  ContaBancariaEntrada,
  DespesaEntrada,
  ReceitaEntrada,
  ImportacaoResumo,
  PreviaExtrato,
  ContribuicaoDetalhe,
  ContribuicaoEntrada,
  EventoEntrada,
  EventoResumo,
  ParcelaEntrada,
  ResultadoConciliacao,
  ResultadoVinculoDespesas,
  ContaEntrada,
  ContaResumo,
  PlanoDeContas,
  Retrato,
  Orcamento,
  ExercicioResumo,
  RubricaResposta,
  RubricaEntrada,
  GeracaoMensalidades,
  FormaPagamento,
  ResultadoEmLote,
  ProjecaoQuadro,
  RelatorioFrequencia,
  RelatorioMarcos,
  RelatorioAgape,
  RelatorioMovimento,
  RelatorioQuadro,
  ResultadoSessao,
  DespesaCandidata,
  BaseMensalidade,
  ItemCopiaDespesa,
  ResultadoCopiaDespesas,
  ResultadoGerarReceitas,
  SessaoNaLista,
  PosicaoAplicacao,
  RespostaLogin,
  VisaoGeral,
  ResumoTesouraria,
  SessaoResumo,
  TotalPorCategoria,
  Trabalho,
  Tronco,
  UsuarioAutenticado,
} from '../types';

const buscar = async <T,>(url: string, params?: Record<string, unknown>): Promise<T> =>
  (await api.get<T>(url, { params })).data;

export const Api = {
  // ── Autenticação ────────────────────────────────────────────────────────
  lojas: () => buscar<LojaPublica[]>('/lojas'),
  lojaPorSubdominio: (subdominio: string) => buscar<LojaPublica>(`/lojas/por-subdominio/${encodeURIComponent(subdominio)}`),

  // ── Identidade da loja (marca do portal) ─────────────────────────────────
  lojaAtual: () => buscar<LojaDetalhe>('/lojas/atual'),

  salvarIdentidadeLoja: async (dados: IdentidadeDaLoja): Promise<LojaDetalhe> =>
    (await api.put<LojaDetalhe>('/lojas/atual', dados)).data,

  enviarBrasao: async (arquivo: File): Promise<LojaDetalhe> => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    // Sem Content-Type explícito: o navegador precisa gerar o boundary.
    return (await api.put<LojaDetalhe>('/lojas/atual/brasao', form)).data;
  },

  removerBrasao: async (): Promise<void> => {
    await api.delete('/lojas/atual/brasao');
  },

  /** Só o administrador: o combinado com a plataforma e o que está em aberto. */
  assinaturaDaLoja: () => buscar<AssinaturaDetalhe>('/lojas/atual/assinatura'),

  /** ZIP com todos os dados da loja — a loja é dona deles (LGPD). */
  exportarLoja: async (): Promise<Blob> =>
    (await api.get<Blob>('/lojas/atual/exportacao', { responseType: 'blob' })).data,

  /** Movimento do período para o contador: `.xlsx`, ou `.zip` com as notas.
   *  Meses em `aaaa-mm`. Juntar as notas baixa cada arquivo do armazenamento,
   *  então o prazo é bem maior que o padrão. */
  exportacaoContador: async (params: { inicio: string; fim: string; documentos: boolean }): Promise<Blob> => {
    try {
      return (
        await api.get<Blob>('/financeiro/exportacao-contador', {
          params,
          responseType: 'blob',
          timeout: 300_000,
        })
      ).data;
    } catch (erro) {
      // Com `responseType: 'blob'` o erro também chega como blob: sem lê-lo, a
      // tela diria só "não foi possível" em vez do motivo que o servidor deu.
      // (Sem importar o axios aqui: o app reaproveita este arquivo com outro cliente.)
      const resposta = (erro as { response?: { data?: unknown } }).response;
      if (resposta?.data instanceof Blob) {
        try {
          resposta.data = JSON.parse(await resposta.data.text());
        } catch {
          // Corpo que não é JSON: fica a mensagem padrão.
        }
      }
      throw erro;
    }
  },

  login: async (loja: string, usuario: string, senha: string): Promise<RespostaLogin> =>
    (await api.post<RespostaLogin>('/auth/login', { loja, usuario, senha })).data,

  eu: () => buscar<UsuarioAutenticado>('/auth/me'),

  trocarSenha: async (senhaAtual: string, senhaNova: string): Promise<void> => {
    await api.post('/auth/senha', { senha_atual: senhaAtual, senha_nova: senhaNova });
  },

  // ── Painel e tesouraria ─────────────────────────────────────────────────
  dashboard: () => buscar<VisaoGeral>('/dashboard'),
  resumoTesouraria: () => buscar<ResumoTesouraria>('/tesouraria/resumo'),
  irmaosTesouraria: (params?: { busca?: string; situacao?: string; ordenar_por?: string }) =>
    buscar<MembroLista[]>('/tesouraria/irmaos', params),

  // ── Quadro ──────────────────────────────────────────────────────────────
  membros: (params?: { busca?: string; grau?: string }) =>
    buscar<MembroLista[]>('/quadro/membros', params),
  perfilMembro: (id: number) => buscar<MembroPerfil>(`/quadro/membros/${id}`),
  projecaoQuadro: () => buscar<ProjecaoQuadro>('/quadro/projecao-quadro'),

  relatorioQuadro: () => buscar<RelatorioQuadro>('/relatorios/quadro'),
  relatorioMarcos: () => buscar<RelatorioMarcos>('/relatorios/marcos'),
  relatorioFrequencia: (ano?: number) =>
    buscar<RelatorioFrequencia>('/relatorios/frequencia', ano ? { ano } : undefined),
  relatorioMovimento: () => buscar<RelatorioMovimento>('/relatorios/movimento'),
  relatorioAgape: (ano?: number) => buscar<RelatorioAgape>('/relatorios/agape', ano ? { ano } : undefined),
  criarAgape: async (dados: AgapeEntrada): Promise<void> => {
    await api.post('/agapes', dados);
  },
  editarAgape: async (id: number, dados: AgapeEntrada): Promise<void> => {
    await api.put(`/agapes/${id}`, dados);
  },
  excluirAgape: async (id: number): Promise<void> => {
    await api.delete(`/agapes/${id}`);
  },

  painelPresencas: (params?: { ano?: number; incluir_inativos?: boolean }) =>
    buscar<PainelPresencas>('/quadro/presencas', params),

  graduacoes: (params?: { busca?: string; grau?: string }) =>
    buscar<MembroGraduacaoLinha[]>('/quadro/graduacoes', params),

  // ── Cadastro do irmão ───────────────────────────────────────────────────
  cadastroMembro: (id: number) => buscar<MembroCadastro>(`/quadro/membros/${id}/cadastro`),

  salvarCadastroMembro: async (id: number, dados: MembroCadastroEntrada): Promise<MembroCadastro> =>
    (await api.put<MembroCadastro>(`/quadro/membros/${id}/cadastro`, dados)).data,

  salvarContatos: async (id: number, contatos: ContatoEntrada[]): Promise<Contato[]> =>
    (await api.put<Contato[]>(`/quadro/membros/${id}/contatos`, contatos)).data,

  salvarConjuge: async (id: number, dados: ConjugeEntrada): Promise<Conjuge> =>
    (await api.put<Conjuge>(`/quadro/membros/${id}/conjuge`, dados)).data,

  removerConjuge: async (id: number): Promise<void> => {
    await api.delete(`/quadro/membros/${id}/conjuge`);
  },

  /** Graus do irmão: o atual é o mais alto registrado. */
  registrarGraduacao: async (id: number, dados: { grau: string; data: string; observacao?: string | null }): Promise<GraduacaoItem[]> =>
    (await api.post<GraduacaoItem[]>(`/quadro/membros/${id}/graduacoes`, dados)).data,
  corrigirGraduacao: async (id: number, grau: string, dados: { data: string; observacao?: string | null }): Promise<GraduacaoItem[]> =>
    (await api.put<GraduacaoItem[]>(`/quadro/membros/${id}/graduacoes/${grau}`, dados)).data,
  removerGraduacao: async (id: number, grau: string): Promise<GraduacaoItem[]> =>
    (await api.delete<GraduacaoItem[]>(`/quadro/membros/${id}/graduacoes/${grau}`)).data,

  /** O saldo que o irmão trazia de antes do exercício. Positivo é dívida. */
  salvarSaldoInicial: async (
    id: number,
    dados: { saldo: string; referencia?: string | null; observacao?: string | null },
  ): Promise<PosicaoFinanceira> =>
    (await api.put<PosicaoFinanceira>(`/quadro/membros/${id}/saldo-inicial`, dados)).data,

  adicionarFilho: async (id: number, dados: FilhoEntrada): Promise<Filho> =>
    (await api.post<Filho>(`/quadro/membros/${id}/filhos`, dados)).data,

  atualizarFilho: async (id: number, filhoId: number, dados: FilhoEntrada): Promise<Filho> =>
    (await api.put<Filho>(`/quadro/membros/${id}/filhos/${filhoId}`, dados)).data,

  removerFilho: async (id: number, filhoId: number): Promise<void> => {
    await api.delete(`/quadro/membros/${id}/filhos/${filhoId}`);
  },

  // ── Retrato do irmão ────────────────────────────────────────────────────
  enviarFotoMembro: async (id: number, arquivo: File): Promise<Retrato> => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    // Sem Content-Type explícito: o navegador precisa gerar o boundary.
    return (await api.put<Retrato>(`/quadro/membros/${id}/foto`, form)).data;
  },

  removerFotoMembro: async (id: number): Promise<void> => {
    await api.delete(`/quadro/membros/${id}/foto`);
  },

  // ── Datas a anunciar (Chancelaria) ──────────────────────────────────────
  datasImportantes: (dias: number) => buscar<DatasImportantes>('/chancelaria/datas', { dias }),

  enviarDatasImportantes: async (): Promise<EnvioDeDatas> =>
    (await api.post<EnvioDeDatas>('/chancelaria/datas/enviar')).data,

  // ── Localidades ─────────────────────────────────────────────────────────
  ufs: () => buscar<Uf[]>('/localidades/ufs'),
  municipios: (params: { uf?: string; busca?: string; limite?: number }) =>
    buscar<MunicipioOpcao[]>('/localidades/municipios', params),
  consultarCep: (cep: string) => buscar<EnderecoPorCep>(`/localidades/cep/${cep}`),

  // ── Sessões, agenda e atas ──────────────────────────────────────────────
  sessoes: () => buscar<SessaoResumo[]>('/sessoes'),
  agenda: (ano?: number, mes?: number) => buscar<AgendaItem[]>('/agenda', { ano, mes }),

  /** Lê o calendário publicado de um arquivo. Não grava nada: devolve as linhas
   *  já confrontadas com a agenda, para a Secretaria conferir.
   *  Tempo limite próprio e maior que o padrão — a leitura por IA de uma tabela
   *  de sessenta linhas passa dos 30s do cliente, e o erro de timeout do axios
   *  esconderia a mensagem útil que o servidor devolve. */
  lerCalendario: async (arquivo: File, ano: number): Promise<LeituraDoCalendario> => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo);
    corpo.append('ano', String(ano));
    return (
      await api.post<LeituraDoCalendario>('/calendario/ler', corpo, { timeout: 300_000 })
    ).data;
  },

  /** Compromisso avulso: lançar, corrigir e tirar um item sem reimportar o semestre. */
  criarCompromisso: async (dados: CompromissoEntrada): Promise<AgendaItem> =>
    (await api.post<AgendaItem>('/calendario/itens', dados)).data,
  editarCompromisso: async (id: number, dados: CompromissoEntrada): Promise<AgendaItem> =>
    (await api.put<AgendaItem>(`/calendario/itens/${id}`, dados)).data,
  removerCompromisso: async (id: number): Promise<void> => {
    await api.delete(`/calendario/itens/${id}`);
  },

  importarCalendario: async (
    ano: number,
    itens: ItemDoCalendarioConfirmado[],
  ): Promise<ResultadoImportacaoCalendario> =>
    (await api.post<ResultadoImportacaoCalendario>('/calendario/importar', { ano, itens })).data,
  atas: (params?: { busca?: string; tipo?: string }) => buscar<Ata[]>('/atas', params),
  ata: (id: number) => buscar<AtaDetalhe>(`/atas/${id}`),
  /** Manda o PDF para a IA ler; demora — é leitura de documento inteiro. */
  lerAta: async (arquivo: File): Promise<LeituraDaAta> => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    return (await api.post<LeituraDaAta>('/atas/ler', form, { timeout: 300_000 })).data;
  },
  criarAta: async (dados: AtaEntrada): Promise<AtaDetalhe> => (await api.post<AtaDetalhe>('/atas', dados)).data,
  editarAta: async (id: number, dados: Partial<Omit<AtaEntrada, 'chave' | 'registrar_tronco' | 'tronco_valor'>>): Promise<AtaDetalhe> =>
    (await api.patch<AtaDetalhe>(`/atas/${id}`, dados)).data,
  refazerResumoAta: async (id: number): Promise<AtaDetalhe> =>
    (await api.post<AtaDetalhe>(`/atas/${id}/resumo`, undefined, { timeout: 300_000 })).data,

  // ── Financeiro ──────────────────────────────────────────────────────────
  exerciciosFinanceiro: () => buscar<number[]>('/financeiro/exercicios'),

  /** Fluxo de caixa do exercício: doze meses sempre, apurados dos lançamentos.
   *  Não aceita recorte de mês — o acumulado só quer dizer algo de ponta a ponta. */
  fluxo: (exercicio?: number) => buscar<FluxoMensal[]>('/financeiro/fluxo', { exercicio }),

  // ── Acesso do irmão ao portal ──────────────────────────────────────────
  papeisDeAcesso: () => buscar<PapelDeAcesso[]>('/acesso/papeis'),
  acessoDoIrmao: (membroId: number) =>
    buscar<AcessoDoIrmao>(`/quadro/membros/${membroId}/acesso`),
  definirAcesso: async (membroId: number, dados: AcessoEntrada): Promise<AcessoDoIrmao> =>
    (await api.put<AcessoDoIrmao>(`/quadro/membros/${membroId}/acesso`, dados)).data,
  /** Senha nova e e-mail com o botão que já entra. A senha não volta para a tela. */
  convidarIrmao: async (membroId: number): Promise<{ email_mascarado: string; usuario: string }> =>
    (await api.post<{ email_mascarado: string; usuario: string }>(`/quadro/membros/${membroId}/acesso/convite`)).data,
  definirSenha: async (
    membroId: number,
    senha: string,
    trocarNoProximoLogin = true,
  ): Promise<AcessoDoIrmao> =>
    (
      await api.put<AcessoDoIrmao>(`/quadro/membros/${membroId}/acesso/senha`, {
        senha,
        trocar_no_proximo_login: trocarNoProximoLogin,
      })
    ).data,

  /** Abre um irmão no quadro. Admin e Secretário. */
  criarMembro: async (dados: MembroNovo): Promise<MembroCadastro> =>
    (await api.post<MembroCadastro>('/quadro/membros', dados)).data,
  /** Apaga o cadastro — o servidor recusa quem já tem histórico. */
  excluirMembro: async (id: number): Promise<void> => {
    await api.delete(`/quadro/membros/${id}`);
  },

  /** Condição do irmão perante a Ordem e benefício sobre a mensalidade. */
  beneficio: (membroId: number) =>
    buscar<BeneficioDoMembro>(`/quadro/membros/${membroId}/beneficio`),
  salvarBeneficio: async (membroId: number, dados: BeneficioEntrada): Promise<BeneficioDoMembro> =>
    (await api.put<BeneficioDoMembro>(`/quadro/membros/${membroId}/beneficio`, dados)).data,
  receitas: (params?: {
    busca?: string;
    competencia?: string;
    categoria?: string;
    exercicio?: number;
  }) => buscar<Lancamento[]>('/financeiro/receitas', params),
  despesas: (params?: {
    busca?: string;
    competencia?: string;
    categoria?: string;
    evento_id?: number;
    sem_evento?: boolean;
    exercicio?: number;
  }) => buscar<Lancamento[]>('/financeiro/despesas', params),
  totaisPorCategoria: (tipo: 'receita' | 'despesa', competencia?: string, exercicio?: number) =>
    buscar<TotalPorCategoria[]>('/financeiro/totais-por-categoria', {
      tipo,
      competencia,
      exercicio,
    }),
  aplicacoes: (exercicio?: number, mes?: number | null) =>
    buscar<PosicaoAplicacao[]>('/financeiro/aplicacoes', { exercicio, mes: mes ?? undefined }),
  tronco: (exercicio?: number, mes?: number | null) =>
    buscar<Tronco>('/financeiro/tronco', { exercicio, mes: mes ?? undefined }),
  cobrirPeloTronco: async (dados: CoberturaTroncoEntrada): Promise<CoberturaTroncoResposta> =>
    (await api.post<CoberturaTroncoResposta>('/financeiro/tronco/cobertura', dados)).data,
  fraternidade: (exercicio?: number, mes?: number | null) =>
    buscar<Fraternidade>('/financeiro/fraternidade', { exercicio, mes: mes ?? undefined }),
  orcamento: (exercicio?: number) => buscar<Orcamento>('/financeiro/orcamento', { exercicio }),

  exerciciosOrcamento: () => buscar<ExercicioResumo[]>('/financeiro/orcamento/exercicios'),

  rubricas: (exercicio: number) =>
    buscar<RubricaResposta[]>(`/financeiro/orcamento/${exercicio}/rubricas`),

  criarRubrica: async (dados: RubricaEntrada): Promise<RubricaResposta> =>
    (await api.post<RubricaResposta>('/financeiro/orcamento/rubricas', dados)).data,

  salvarRubrica: async (id: number, dados: RubricaEntrada): Promise<RubricaResposta> =>
    (await api.put<RubricaResposta>(`/financeiro/orcamento/rubricas/${id}`, dados)).data,

  removerRubrica: async (id: number): Promise<void> => {
    await api.delete(`/financeiro/orcamento/rubricas/${id}`);
  },

  copiarOrcamento: async (
    exercicio: number,
    origem: number,
    reajuste = 0,
  ): Promise<RubricaResposta[]> =>
    (
      await api.post<RubricaResposta[]>(
        `/financeiro/orcamento/${exercicio}/copiar-de/${origem}`,
        null,
        { params: { reajuste_percentual: reajuste } },
      )
    ).data,

  reajustarRubricas: async (ids: number[], percentual: string): Promise<ResultadoEmLote> =>
    (
      await api.post<ResultadoEmLote>('/financeiro/orcamento/rubricas/reajustar', {
        ids,
        percentual,
      })
    ).data,

  removerRubricas: async (ids: number[]): Promise<ResultadoEmLote> =>
    (await api.post<ResultadoEmLote>('/financeiro/orcamento/rubricas/remover', { ids })).data,

  reajustarDespesas: async (ids: number[], percentual: string): Promise<ResultadoEmLote> =>
    (
      await api.post<ResultadoEmLote>('/financeiro/despesas/lote/reajustar', { ids, percentual })
    ).data,

  pagarDespesas: async (
    ids: number[],
    dataPagamento: string,
    contaBancariaId: number | null,
    formaPagamento: FormaPagamento | null,
  ): Promise<ResultadoEmLote> =>
    (
      await api.post<ResultadoEmLote>('/financeiro/despesas/lote/pagar', {
        ids,
        data_pagamento: dataPagamento,
        conta_bancaria_id: contaBancariaId,
        forma_pagamento: formaPagamento,
      })
    ).data,

  excluirDespesas: async (ids: number[]): Promise<ResultadoEmLote> =>
    (await api.post<ResultadoEmLote>('/financeiro/despesas/lote/excluir', { ids })).data,

  reajustarReceitas: async (ids: number[], percentual: string): Promise<ResultadoEmLote> =>
    (
      await api.post<ResultadoEmLote>('/financeiro/receitas/lote/reajustar', { ids, percentual })
    ).data,

  /** Baixa em lote. O caminho normal depois de gerar as mensalidades: o mês
   *  inteiro entra previsto e cai junto quando a tesouraria confere. */
  receberReceitas: async (
    ids: number[],
    dataRecebimento: string,
    contaBancariaId: number | null,
    formaPagamento: FormaPagamento | null,
  ): Promise<ResultadoEmLote> =>
    (
      await api.post<ResultadoEmLote>('/financeiro/receitas/lote/receber', {
        ids,
        data_recebimento: dataRecebimento,
        conta_bancaria_id: contaBancariaId,
        forma_pagamento: formaPagamento,
      })
    ).data,

  excluirReceitas: async (ids: number[]): Promise<ResultadoEmLote> =>
    (await api.post<ResultadoEmLote>('/financeiro/receitas/lote/excluir', { ids })).data,

  // ── Chancelaria ─────────────────────────────────────────────────────────
  /** Só id e nome: a tela de presença não tem o que fazer com saldo. */
  irmaosParaEscolher: () => buscar<IrmaoParaEscolher[]>('/chancelaria/irmaos'),

  sessoesChancelaria: (ano?: number) =>
    buscar<SessaoNaLista[]>('/chancelaria/sessoes', ano ? { ano } : undefined),

  /** Só transcreve e confere com o quadro — não grava nada.
   *
   *  Timeout próprio, e generoso: ler uma folha cheia de nomes manuscritos
   *  passa longe dos 30s do padrão da instância, e o front desistindo primeiro
   *  troca a mensagem útil do servidor ("use uma foto menor") por um erro de
   *  rede genérico. Fica acima do limite do backend de propósito, para quem
   *  responde ao usuário ser sempre o servidor. */
  lerFotoPresenca: async (arquivo: File, anoPadrao?: number): Promise<LeituraDaFoto> => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo);
    if (anoPadrao) corpo.append('ano_padrao', String(anoPadrao));
    return (
      await api.post<LeituraDaFoto>('/chancelaria/sessoes/ler-foto', corpo, {
        timeout: 300_000,
      })
    ).data;
  },

  registrarSessao: async (dados: {
    data: string;
    tipo: TipoSessao;
    grau: number | null;
    hora?: string | null;
    titulo?: string | null;
    conta_frequencia: boolean;
    presentes: number[];
    substituir: boolean;
    lista_presenca_chave?: string | null;
    /** Ausente = não mexe nos visitantes já gravados. */
    visitantes?: VisitanteEntrada[];
    lista_visitantes_chave?: string | null;
  }): Promise<ResultadoSessao> =>
    (await api.post<ResultadoSessao>('/chancelaria/sessoes', dados)).data,

  /** A folha de visitantes (separada da do quadro), lida pela IA. Não grava. */
  lerVisitantes: async (arquivo: File): Promise<LeituraDosVisitantes> => {
    const corpo = new FormData();
    corpo.append('arquivo', arquivo);
    return (await api.post<LeituraDosVisitantes>('/chancelaria/sessoes/ler-visitantes', corpo, { timeout: 300_000 })).data;
  },
  visitantesDaSessao: (sessaoId: number) => buscar<VisitanteDaSessao[]>(`/chancelaria/sessoes/${sessaoId}/visitantes`),
  salvarVisitantes: async (sessaoId: number, visitantes: VisitanteEntrada[], chave?: string | null): Promise<VisitanteDaSessao[]> =>
    (await api.put<VisitanteDaSessao[]>(`/chancelaria/sessoes/${sessaoId}/visitantes`, { visitantes, lista_visitantes_chave: chave ?? null })).data,
  fotoDosVisitantes: async (sessaoId: number): Promise<string> => {
    const r = await api.get(`/chancelaria/sessoes/${sessaoId}/lista-visitantes`, { responseType: 'blob' });
    return URL.createObjectURL(r.data as Blob);
  },

  /** Bytes da foto pela própria API: o S3 é privado e nenhuma URL dele
   *  chega ao navegador. */
  fotoDaLista: async (sessaoId: number, pagina = 1): Promise<string> => {
    const r = await api.get(`/chancelaria/sessoes/${sessaoId}/lista-presenca`, {
      params: pagina > 1 ? { pagina } : undefined,
      responseType: 'blob',
    });
    return URL.createObjectURL(r.data as Blob);
  },

  removerSessao: async (id: number): Promise<void> => {
    await api.delete(`/chancelaria/sessoes/${id}`);
  },

  baseMensalidade: (exercicio: number) =>
    buscar<BaseMensalidade>('/financeiro/receitas/base-mensalidade', { exercicio }),

  /** Fixa a mensalidade do exercício e deriva dela as competências e a receita
   *  prevista do ano. Diferente de `gerarMensalidades`, do orçamento, que parte
   *  do valor atual de cada irmão e aplica um reajuste. */
  fixarMensalidade: async (
    exercicio: number,
    valorMensalidade: string,
    opcoes: { sobrescrever?: boolean; simular?: boolean } = {},
  ): Promise<ResultadoGerarReceitas> =>
    (
      await api.post<ResultadoGerarReceitas>('/financeiro/receitas/gerar-mensalidades', {
        exercicio,
        valor_mensalidade: valorMensalidade,
        sobrescrever: opcoes.sobrescrever ?? false,
        simular: opcoes.simular ?? true,
      })
    ).data,

  gerarMensalidades: async (dados: {
    exercicio: number;
    reajuste_percentual: string;
    sobrescrever: boolean;
    simular: boolean;
  }): Promise<GeracaoMensalidades> =>
    (await api.post<GeracaoMensalidades>('/financeiro/orcamento/mensalidades/gerar', dados)).data,

  // ── Cadastro de despesas ────────────────────────────────────────────────
  contasBancarias: (apenasAtivas = true) =>
    buscar<ContaBancaria[]>('/financeiro/contas-bancarias', { apenas_ativas: apenasAtivas }),

  criarContaBancaria: async (dados: ContaBancariaEntrada): Promise<ContaBancaria> =>
    (await api.post<ContaBancaria>('/financeiro/contas-bancarias', dados)).data,

  salvarContaBancaria: async (id: number, dados: ContaBancariaEntrada): Promise<ContaBancaria> =>
    (await api.put<ContaBancaria>(`/financeiro/contas-bancarias/${id}`, dados)).data,

  inativarContaBancaria: async (id: number): Promise<void> => {
    await api.delete(`/financeiro/contas-bancarias/${id}`);
  },

  criarReceita: async (dados: ReceitaEntrada): Promise<Lancamento> =>
    (await api.post<Lancamento>('/financeiro/receitas', dados)).data,

  salvarReceita: async (id: number, dados: ReceitaEntrada): Promise<Lancamento> =>
    (await api.put<Lancamento>(`/financeiro/receitas/${id}`, dados)).data,

  excluirReceita: async (id: number): Promise<void> => {
    await api.delete(`/financeiro/receitas/${id}`);
  },

  // ── Extrato bancário ────────────────────────────────────────────────────
  previaExtrato: async (contaId: number, arquivo: File): Promise<PreviaExtrato> => {
    const form = new FormData();
    form.append('conta_bancaria_id', String(contaId));
    form.append('arquivo', arquivo);
    // Formato que não é dos exports do BB é lido pela IA: PDF e foto passam
    // fácil dos 30 s padrão do cliente.
    return (await api.post<PreviaExtrato>('/financeiro/extrato/previa', form, { timeout: 300_000 })).data;
  },

  /** `escolhas`: por `id_operacao`, o id da despesa prevista que recebe a
   * baixa, ou `null` para lançar como novo. Linha ausente fica com o que o
   * servidor decidir sozinho (a baixa segura, ou lançamento novo). */
  importarExtrato: async (
    contaId: number,
    arquivo: File,
    escolhas: Record<string, number | null> = {},
    /** Conta do plano escolhida na prévia, por `id_operacao` (id da conta). */
    contas: Record<string, number> = {},
  ): Promise<PreviaExtrato> => {
    const form = new FormData();
    form.append('conta_bancaria_id', String(contaId));
    form.append('arquivo', arquivo);
    form.append('ignorar_repetidos', 'true');
    form.append('escolhas', JSON.stringify(escolhas));
    form.append('contas', JSON.stringify(contas));
    return (await api.post<PreviaExtrato>('/financeiro/extrato/importar', form, { timeout: 300_000 })).data;
  },

  importacoesExtrato: () => buscar<ImportacaoResumo[]>('/financeiro/extrato/importacoes'),

  desfazerImportacao: async (id: number): Promise<void> => {
    await api.delete(`/financeiro/extrato/importacoes/${id}`);
  },

  despesasCandidatasCopia: (exercicioDestino: number, origem?: number) =>
    buscar<DespesaCandidata[]>('/financeiro/despesas/candidatas-copia', {
      exercicio_destino: exercicioDestino,
      origem,
    }),

  copiarDespesas: async (
    exercicioDestino: number,
    itens: ItemCopiaDespesa[],
  ): Promise<ResultadoCopiaDespesas> =>
    (
      await api.post<ResultadoCopiaDespesas>('/financeiro/despesas/copiar', {
        exercicio_destino: exercicioDestino,
        itens,
      })
    ).data,

  criarDespesa: async (dados: DespesaEntrada): Promise<Lancamento> =>
    (await api.post<Lancamento>('/financeiro/despesas', dados)).data,

  salvarDespesa: async (id: number, dados: DespesaEntrada): Promise<Lancamento> =>
    (await api.put<Lancamento>(`/financeiro/despesas/${id}`, dados)).data,

  excluirDespesa: async (id: number): Promise<void> => {
    await api.delete(`/financeiro/despesas/${id}`);
  },

  // ── Fornecedores ────────────────────────────────────────────────────────
  fornecedores: (params?: {
    busca?: string;
    tipo?: TipoFornecedor;
    ramo?: string;
    apenas_ativos?: boolean;
  }) => buscar<Fornecedor[]>('/fornecedores', params),

  fornecedor: (id: number) => buscar<FornecedorDetalhe>(`/fornecedores/${id}`),

  ramosFornecedor: () => buscar<string[]>('/fornecedores/ramos'),

  contrapartesPendentes: () =>
    buscar<ContraparteNaoVinculada[]>('/fornecedores/pendencias'),

  criarFornecedor: async (dados: FornecedorEntrada): Promise<FornecedorDetalhe> =>
    (await api.post<FornecedorDetalhe>('/fornecedores', dados)).data,

  salvarFornecedor: async (id: number, dados: FornecedorEntrada): Promise<FornecedorDetalhe> =>
    (await api.put<FornecedorDetalhe>(`/fornecedores/${id}`, dados)).data,

  inativarFornecedor: async (id: number): Promise<void> => {
    await api.delete(`/fornecedores/${id}`);
  },

  salvarContatosFornecedor: async (id: number, contatos: ContatoEntrada[]): Promise<Contato[]> =>
    (await api.put<Contato[]>(`/fornecedores/${id}/contatos`, contatos)).data,

  vincularDespesa: async (
    lancamentoId: number,
    fornecedorId: number | null,
  ): Promise<FornecedorDetalhe | null> =>
    (
      await api.put<FornecedorDetalhe | null>(`/fornecedores/despesas/${lancamentoId}`, {
        fornecedor_id: fornecedorId,
        registrar_apelido: true,
      })
    ).data,

  /** Resolve de uma vez todas as despesas com o mesmo texto no extrato. */
  conciliarContraparte: async (
    fornecedorId: number,
    contraparte: string,
  ): Promise<FornecedorDetalhe> =>
    (
      await api.post<FornecedorDetalhe>(
        `/fornecedores/${fornecedorId}/conciliar`,
        null,
        { params: { contraparte } },
      )
    ).data,

  // ── Plano de contas ─────────────────────────────────────────────────────
  planoDeContas: (apenasAtivas = false) =>
    buscar<PlanoDeContas>('/plano-contas', { apenas_ativas: apenasAtivas }),

  /** Só para loja sem plano nenhum: cria o plano padrão de uma vez. */
  usarPlanoPadrao: async (): Promise<PlanoDeContas> =>
    (await api.post<PlanoDeContas>('/plano-contas/padrao')).data,

  contas: (params?: { tipo?: 'receita' | 'despesa'; apenas_analiticas?: boolean }) =>
    buscar<ContaResumo[]>('/plano-contas/contas', params),

  criarConta: async (dados: ContaEntrada): Promise<ContaResumo> =>
    (await api.post<ContaResumo>('/plano-contas/contas', dados)).data,

  salvarConta: async (id: number, dados: ContaEntrada): Promise<ContaResumo> =>
    (await api.put<ContaResumo>(`/plano-contas/contas/${id}`, dados)).data,

  inativarConta: async (id: number): Promise<void> => {
    await api.delete(`/plano-contas/contas/${id}`);
  },

  reclassificarConta: async (id: number, destinoId: number): Promise<ContaResumo> =>
    (
      await api.post<ContaResumo>(`/plano-contas/contas/${id}/reclassificar`, null, {
        params: { destino_id: destinoId },
      })
    ).data,

  // ── Eventos ─────────────────────────────────────────────────────────────
  eventos: (incluirExcluidos = false) =>
    buscar<EventoResumo[]>('/eventos', { incluir_excluidos: incluirExcluidos }),

  evento: (slug: string) => buscar<Evento>(`/eventos/${slug}/apuracao`),

  criarEvento: async (dados: EventoEntrada): Promise<EventoResumo> =>
    (await api.post<EventoResumo>('/eventos', dados)).data,

  salvarEvento: async (id: number, dados: EventoEntrada): Promise<EventoResumo> =>
    (await api.put<EventoResumo>(`/eventos/${id}`, dados)).data,

  excluirEvento: async (id: number): Promise<void> => {
    await api.delete(`/eventos/${id}`);
  },

  restaurarEvento: async (id: number): Promise<EventoResumo> =>
    (await api.post<EventoResumo>(`/eventos/${id}/restaurar`)).data,

  contribuicoes: (eventoId: number) =>
    buscar<ContribuicaoDetalhe[]>(`/eventos/${eventoId}/contribuicoes`),

  gerarCotas: async (eventoId: number): Promise<ContribuicaoDetalhe[]> =>
    (await api.post<ContribuicaoDetalhe[]>(`/eventos/${eventoId}/cotas`)).data,

  criarContribuicao: async (
    eventoId: number,
    dados: ContribuicaoEntrada,
  ): Promise<ContribuicaoDetalhe> =>
    (await api.post<ContribuicaoDetalhe>(`/eventos/${eventoId}/contribuicoes`, dados)).data,

  salvarContribuicao: async (
    eventoId: number,
    id: number,
    dados: ContribuicaoEntrada,
  ): Promise<ContribuicaoDetalhe> =>
    (await api.put<ContribuicaoDetalhe>(`/eventos/${eventoId}/contribuicoes/${id}`, dados)).data,

  salvarParcelas: async (
    eventoId: number,
    id: number,
    parcelas: ParcelaEntrada[],
  ): Promise<ContribuicaoDetalhe> =>
    (
      await api.put<ContribuicaoDetalhe>(
        `/eventos/${eventoId}/contribuicoes/${id}/parcelas`,
        parcelas,
      )
    ).data,

  removerContribuicao: async (eventoId: number, id: number): Promise<void> => {
    await api.delete(`/eventos/${eventoId}/contribuicoes/${id}`);
  },

  conciliarEvento: async (eventoId: number): Promise<ResultadoConciliacao> =>
    (await api.post<ResultadoConciliacao>(`/eventos/${eventoId}/conciliar`)).data,

  vincularDespesasEvento: async (
    eventoId: number,
    despesaIds: number[],
  ): Promise<ResultadoVinculoDespesas> =>
    (
      await api.put<ResultadoVinculoDespesas>(`/eventos/${eventoId}/despesas/vincular`, {
        despesa_ids: despesaIds,
      })
    ).data,

  desvincularDespesaEvento: async (eventoId: number, despesaId: number): Promise<void> => {
    await api.delete(`/eventos/${eventoId}/despesas/${despesaId}`);
  },

  // ── Documentos ──────────────────────────────────────────────────────────
  gob: () => buscar<Gob>('/gob'),

  // ── Arquivos ────────────────────────────────────────────────────────────
  arquivoDocumentoGob: (codigo: string) =>
    buscar<Arquivo>(`/gob/documentos/${codigo}/arquivo`),
  arquivoAta: (id: number) => buscar<Arquivo>(`/atas/${id}/arquivo`),

  // ── Trabalhos ───────────────────────────────────────────────────────────
  trabalhos: (params?: { busca?: string; grau?: number }) =>
    buscar<Trabalho[]>('/trabalhos', params),
  trabalho: (id: number) => buscar<Trabalho>(`/trabalhos/${id}`),
  /** Publica o trabalho. Multipart porque o caminho normal é anexar a peça
   *  pronta em PDF ou Word — e `texto` passa a ser opcional por isso. */
  publicarTrabalho: async (dados: {
    titulo: string;
    texto?: string;
    grau_minimo: number;
    autor_nome?: string;
    arquivos?: File[];
  }): Promise<Trabalho> => {
    const corpo = new FormData();
    corpo.append('titulo', dados.titulo);
    corpo.append('grau_minimo', String(dados.grau_minimo));
    if (dados.texto) corpo.append('texto', dados.texto);
    if (dados.autor_nome) corpo.append('autor_nome', dados.autor_nome);
    for (const a of dados.arquivos ?? []) corpo.append('arquivos', a);
    // Upload de peça escaneada passa fácil dos 30s padrão do cliente.
    return (await api.post<Trabalho>('/trabalhos', corpo, { timeout: 180_000 })).data;
  },
  acrescentarArquivos: async (id: number, arquivos: File[]): Promise<Trabalho> => {
    const corpo = new FormData();
    for (const a of arquivos) corpo.append('arquivos', a);
    return (
      await api.post<Trabalho>(`/trabalhos/${id}/arquivos`, corpo, { timeout: 180_000 })
    ).data;
  },
  removerArquivoDoTrabalho: async (id: number, arquivoId: number): Promise<Trabalho> =>
    (await api.delete<Trabalho>(`/trabalhos/${id}/arquivos/${arquivoId}`)).data,
  /** Apaga o trabalho e os arquivos dele. Não tem volta. */
  excluirTrabalho: async (id: number): Promise<void> => {
    await api.delete(`/trabalhos/${id}`);
  },
};
